import { SocialServiceOfferDetailDialogStore } from './social-service-offer-detail-dialog.store';
import { fb } from '../../../../../modules/shared/shared.global-service';
import { Component, effect, inject, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { DateInputComponent } from '../../../../kit/input/date-input/date-input.component';
import { NumberInputComponent } from '../../../../kit/input/text-input/number-input.component';
import { SelectInputComponent } from '../../../../kit/input/select-input/select-input.component';
import { MultiSelectInputComponent } from '../../../../kit/input/multi-select-input/multi-select-input.component';
import { TagsInputComponent } from '../../../../kit/input/tags-input/tags-input.component';
import {
    InputWysiwygComponent,
} from '../../../../../modules/shared/components/inputs/input-wysiwyg/input-wysiwyg.component';
import { BaseDialogComponent } from '../../../../kit/base-dialog/base-dialog.component';
import { AppFormGroup } from '../../../../../utils/form.utils';
import { FilterBuilder } from '@libs/shared/utils/grid.builder';
import { getProjectsFrag, getServiceAreasFrag, getTargetsFrag } from './social-service-offer-detail-dialog.query';

@Component({
    selector: 'app-social-service-offer-detail-dialog',
    templateUrl: './social-service-offer-detail-dialog.component.html',
    styleUrl: './social-service-offer-detail-dialog.component.scss',
    standalone: true,
    providers: [SocialServiceOfferDetailDialogStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        DateInputComponent,
        NumberInputComponent,
        SelectInputComponent,
        MultiSelectInputComponent,
        TagsInputComponent,
        InputWysiwygComponent,
    ],
})
export class SocialServiceOfferDetailDialogComponent extends BaseDialogComponent {

    public readonly store = inject(SocialServiceOfferDetailDialogStore);

    protected readonly applicationsInput = viewChild.required<NumberInputComponent>('applicationsInput');
    protected readonly durationNoteInput = viewChild<NumberInputComponent>('durationNoteInput');
    protected readonly durationPreparationInput = viewChild<NumberInputComponent>('durationPreparationInput');
    protected readonly durationSelfReflectionInput = viewChild<NumberInputComponent>('durationSelfReflectionInput');
    protected readonly tagInput = viewChild<TagsInputComponent>(TagsInputComponent);

    protected prefix = 'workData.offer.';

    protected form = new AppFormGroup({
        dateFrom: fb.control<Date | null>(null),
        dateTo: fb.control<Date | null>(null),
        presence: fb.control(''),
        applications: fb.control<number>(0),
        executions: fb.control<number>(0),
        durationOther: fb.control<number>(0),
        durationNote: fb.control<number>(0),
        durationPreparation: fb.control<number>(0),
        durationSelfReflection: fb.control<number>(0),
        tags: fb.control<string[]>([]),
        targets: fb.control<number[]>([]),
        projects: fb.control<number[]>([]),
        areas: fb.control<number[]>([]),
        note: fb.control('', { nonNullable: true }),
        type: fb.control(''),
        users: fb.control<string[]>([]),
    });

    protected dateToChange = this.form.signalCodeChange<Date>('dateTo');
    protected dateFromChange = this.form.signalCodeChange<Date>('dateFrom');
    protected showDurationOther = this.dialogConfig.data.showDurationOther ?? false;

    constructor() {
        super();

        this.store.serviceId.set(this.dialogConfig.data.serviceId);
        this.form.patchValue({
            dateFrom: this.dialogConfig.data.dateFrom || new Date(),
            dateTo: this.dialogConfig.data.dateTo || new Date(),
            type: this.dialogConfig.data.type || '',
            note: this.dialogConfig.data.note || '',
            presence: this.dialogConfig.data.presence || '',
            applications: this.dialogConfig.data.applications || 0,
            executions: this.dialogConfig.data.executions || 0,
            durationNote: this.dialogConfig.data.durationNote || 0,
            durationPreparation: this.dialogConfig.data.durationPreparation || 0,
            durationSelfReflection: this.dialogConfig.data.durationSelfReflection || 0,
            durationOther:
                (this.dialogConfig.data.durationNote || 0)
                + (this.dialogConfig.data.durationPreparation || 0)
                + (this.dialogConfig.data.durationSelfReflection || 0)
            ,
            tags: this.dialogConfig.data.tags || [],
            areas: this.dialogConfig.data.areas || [],
            users: this.dialogConfig.data.users || [],
            targets: (this.dialogConfig.data.targets || []).map((it: any) => it.targetId),
            projects: (this.dialogConfig.data.projects || []).map((it: any) => typeof it === 'number' ? it : it.projectId),
        });

        const filter = new FilterBuilder({ serviceId: this.dialogConfig.data.serviceId });
        filter.validAt(this.dialogConfig.data.dateFrom || new Date());
        this.store.api.getServiceSocialAreas(filter.singlePage(), getServiceAreasFrag);

        this.store.api.getSocialProjects(
            new FilterBuilder({ serviceId: this.dialogConfig.data.serviceId }).singlePage(),
            getProjectsFrag,
        );

        this.store.api.getIndividualPlans(
            new FilterBuilder()
                .folderId(this.dialogConfig.data.folderId)
                .sort('date', 'DESC')
                .limit(1)
                .filter()
            ,
            getTargetsFrag,
        );

        const durations = effect(() => {
            if (!this.showDurationOther) {
                durations.destroy();
                return;
            }
            this.durationNoteInput()?.setValue(this.dialogConfig.data.durationNote || 0);
            this.durationSelfReflectionInput()?.setValue(this.dialogConfig.data.durationSelfReflection || 0);
            this.durationPreparationInput()?.setValue(this.dialogConfig.data.durationPreparation || 0);
        });

        effect(() => {
            this.tagInput()?.setValue(this.dialogConfig.data.tags || []);
        });

        effect(() => {
            const dateFromChange = this.dateFromChange();
            const dateToChange = this.dateToChange();

            if (dateFromChange.isCodeUpdate || dateToChange.isCodeUpdate) return;

            const from = dateFromChange.value;
            const to = dateToChange.value;
            if (!from || !to) return;

            const applications = (to.getTime() - from.getTime()) / 60_000;
            this.form.patchValueCode({ applications });
            this.applicationsInput().setValue(applications);
        });
    }

    public override save(): void {
        this.dialogRef.close({
            ...this.form.value,
            targets: (this.form.value.targets || []).map((targetId) => ({
                folderId: this.dialogConfig.data.folderId,
                targetId,
            })),
        });
    }

    protected setDuration(duration?: number): void {
        const from = this.dateFromChange().value;
        if (!from) return;

        const dateTo = new Date(from);
        dateTo.setMinutes(dateTo.getMinutes() + (duration || 0));
        this.form.patchValue({ dateTo });
    }

    protected setValue(value: any, key: string): void {
        switch (key) {
            case 'durationNote':
            case 'durationPreparation':
            case 'durationSelfReflection':
                this.form.controls[key].setValue(value);
                this.form.controls.durationOther.setValue(
                    (this.form.value.durationNote || 0)
                    + (this.form.value.durationPreparation || 0)
                    + (this.form.value.durationSelfReflection || 0),
                );
                break;
        }
    }

    protected queryTags(search: string): void {
        this.store.api.searchWorkOfferTags({
            search,
            limit: 100,
            serviceId: this.dialogConfig.data.serviceId,
        });
    }

}
